import { toast } from '@/hooks/use-toast';
import { pdkAxios } from '@/lib/pdk-axios';
import { db } from '@/models/db';
import { vehicleImageStore } from '@/models/stores/vehicle-images';
import type { RawImage, VehicleImage } from './types';

type UploadResponse = {
  object_key: string;
};

type CheckIfImagesAreProcessedResponse = {
  raw_processed_images: RawImage[];
  is_processed: boolean;
};

type CheckIfImageIsReplacedResponse = {
  is_processed: boolean;
  new_timestamp: number;
};

/**
 * At the moment we don't have delta packets for uploaded images, meaning: we have to figure out ourselves when the image
 * is uploaded so we can display it in the UI. This variable is here to solve that problem!
 *
 * You might wonder "why not just define method with function and let them be on their own?", and this is because we wanted
 * to have "one umbrella" of functions that clearly communicates that these methods are not the be used outside of vehicle image
 * upload context.
 */
export const ImageUploadHelper = {
  /**
   * This is regarded as 1 step in image upload process.
   * It performs vehicle image upload one-by-one but the promise will resolve only when all images are uploaded.
   *
   * @param listingID     - Listing to which images need to be uploaded.
   * @param vehicleImages - All images from the gallery context. This is done mainly because we wanted to avoid direct state
   *                        modification, and hide away how images are being manipulated in order to perform an upload.
   * @param onImageUpload - Called whenever an image is uploaded. We need this in order to show user progressively how many images
   *                        are uploaded instead of waiting for whole batch to upload in order to see progress.
   */
  async UploadVehicleImages(
    listingID: string,
    vehicleImages: VehicleImage[],
    onImageUpload: (vehicleImages: VehicleImage[]) => void,
  ): Promise<VehicleImage[]> {
    const vehicleImagesCopy = vehicleImages.map((img) => ({ ...img }));
    const imagesToUpload = vehicleImagesCopy.filter(
      (img) => img.type === 'upload_prepare',
    );

    const imageUploadRequests: Promise<void>[] = [];
    for (const img of imagesToUpload) {
      if (!img.file) {
        continue;
      }

      const formData = new FormData();
      formData.append('file', img.file);

      imageUploadRequests.push(
        new Promise(async (resolve, reject) => {
          try {
            const response = await pdkAxios.postForm<UploadResponse>(
              `/inventory/listing/${listingID}/upload`,
              formData,
              { timeout: 300000 }, // 5min
            );
            // The response gives back an `object_key` so we can use it to ask server "is this image processed?"
            img.objectKey = response.data.object_key;
            img.uploaded = true;
            onImageUpload(vehicleImagesCopy);
            resolve();
          } catch (err) {
            reject(err);
          }
        }),
      );
    }

    await Promise.all(imageUploadRequests);

    return vehicleImagesCopy;
  },

  /**
   * This is regarded as 2 step in image upload process.
   * It watches if images that were uploaded have been processed by Lambda.
   *
   * This is important step because when we perform upload we just upload image to S3, but we don't create metadata record in DB.
   * Because of this behaviour FE is clueless about what is happening to the image we uploaded, and thus we can't give a proper UI
   * feedback to the user.
   * This method is basically addressing that concern by constantly asking server "hey, did processing of these images completed?".
   *
   * Background on why we use Lambda for image processing instead of server:
   * When image is uploaded a Lambda is triggered which creates the metadata record in DB and performs generation of blur hash of an image.
   * The blur hash generation have been a pain point in the past due to the high memory consumption, which was causing server crashes,
   * especially when there's multiple images uploaded. And this is why image processing have been moved to Lambda.
   *
   * @param vehicleImages - All images from the gallery context.
   */
  async WatchIfUploadedImagesAreProcessed(
    vehicleImages: VehicleImage[],
  ): Promise<VehicleImage[]> {
    return new Promise((resolve, reject) => {
      const vehicleImagesCopy = vehicleImages.map((img) => ({ ...img }));

      const objectKeys = vehicleImagesCopy
        .filter((img) => img.type === 'upload_prepare')
        .map((img) => img.objectKey);
      const startTime = new Date().getTime();
      const timeoutLimit = 1200000; // 20min

      // Because we can't predict when the Lambda will process all images we have to create an interval that will check over time
      // if images are processed, and for this we have a dedicated API endpoint which will answer this question.
      const intervalHandle = setInterval(async () => {
        try {
          const response =
            await pdkAxios.post<CheckIfImagesAreProcessedResponse>(
              `/inventory/listing/images/check-if-processed`,
              { object_keys: objectKeys },
              { timeout: 60000 }, // 1min
            );

          if (response.status >= 400) {
            throw Error(`${response.data}`);
          }

          // Once the server answers that it processed all images, we'll also get the image themselves, and this way we can finally
          // grab their ID and use it to further update an image, such as its order index!
          if (response.data.is_processed) {
            const upadtedVehicleImages = vehicleImagesCopy.map((img) => {
              const rawProcessedImage = response.data.raw_processed_images.find(
                (processedImg) => processedImg.object_key == img.objectKey,
              );
              if (rawProcessedImage) {
                img.id = `${rawProcessedImage.id}`;
              }

              return img;
            });

            clearInterval(intervalHandle);
            resolve(upadtedVehicleImages);
          }

          if (startTime - new Date().getTime() >= timeoutLimit) {
            clearInterval(intervalHandle);
            reject(new Error('Image processor watcher timed out...'));
          }
        } catch (err) {
          reject(err);
        }
      }, 1000);
    });
  },

  /**
   * This is regarded as 3 step in image upload process.
   * Because the vehicle images array is what user also sees on the screen, the images are ordered by the index at
   * which they exist within the array.
   *
   * This step is necessary because user can move images anywhere within the gallery before committing an upload!
   *
   * @param vehicleImages - All images from the gallery context.
   */
  async ReOrderAllVehicleImages(vehicleImages: VehicleImage[]): Promise<void> {
    try {
      const payload = vehicleImages.map((image) => ({
        image_id: +image.id,
        order_index: image.order_index,
      }));

      await pdkAxios.put(
        `/inventory/listing/images/re-order`,
        payload,
        { timeout: 20000 }, // 20s
      );
    } catch (err) {
      console.error(err);
      toast({
        title: `Failed to re-order images`,
        description: 'Unexpected error ocurred.',
        variant: 'destructive',
      });
    }
  },

  /**
   * This is regarded as 4 step in image upload process, the final step!
   * It basically loads all images from the server once again to make sure we have latest version of all images.
   * Since this method also calls vehicle image sotre, it also refreshes the store and IndexDB records.
   *
   * @param vehicleListingID - All images from the gallery context.
   */
  async ReloadImages(vehicleListingID: string): Promise<void> {
    await vehicleImageStore.loadImagesForListing(vehicleListingID, true);
  },

  async ReplaceImage(img: VehicleImage): Promise<VehicleImage> {
    try {
      const imgCopy: VehicleImage = { ...img };
      if (!imgCopy.file) {
        return img;
      }

      const formData = new FormData();
      formData.append('file', imgCopy.file);

      const response = await pdkAxios.postForm<UploadResponse>(
        `/inventory/listing/replace-image/${img.id}`,
        formData,
        { timeout: 180000 }, // 3min
      );
      if (response.status >= 400) {
        throw Error(`${response.data}`);
      }
      imgCopy.objectKey = response.data.object_key;

      const startTime = new Date().getTime();
      const timeoutLimit = 180000; // 3min
      await new Promise<void>((resolve, reject) => {
        const intervalHandle = setInterval(async () => {
          const response = await pdkAxios.post<CheckIfImageIsReplacedResponse>(
            `/inventory/listing/images/check-if-replaced`,
            { image_id: +img.id, current_timestamp: img.timestamp },
          );

          if (response.status >= 400) {
            throw reject(response.data);
          }

          if (response.data.is_processed) {
            clearInterval(intervalHandle);
            imgCopy.timestamp = response.data.new_timestamp;
            resolve();
          }

          if (startTime - new Date().getTime() >= timeoutLimit) {
            clearInterval(intervalHandle);
            reject(new Error('Image processor watcher timed out...'));
          }
        }, 1000);
      });

      db.vehicle_listing_images.update(imgCopy.id, {
        timestamp: imgCopy.timestamp,
      });

      return imgCopy;
    } catch (error) {
      return Promise.reject(error);
    }
  },
};
